/**
 * Image Compression Utility for Payment Screenshots
 * Enforces strict compression and validation rules before upload
 */

/**
 * Compress and validate image for payment screenshot upload
 * @param {File} file - Original image file
 * @returns {Promise<{file: File, size: number, isValid: boolean, error?: string}>}
 */
export const compressPaymentScreenshot = async (file) => {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Invalid file type. Only JPEG, PNG, WebP, or PDF allowed.'
      };
    }

    // PDF files don't need compression
    if (file.type === 'application/pdf') {
      if (file.size > 5 * 1024 * 1024) { // 5MB
        return {
          isValid: false,
          error: 'PDF file must be under 5 MB.'
        };
      }
      return {
        file,
        size: file.size,
        isValid: true
      };
    }

    // Check raw file size
    if (file.size > 5 * 1024 * 1024) { // 5MB
      return {
        isValid: false,
        error: 'Image must be under 5 MB before compression.'
      };
    }

    // Load image
    const img = await loadImage(file);

    // Validate minimum dimensions
    if (img.width < 480) {
      return {
        isValid: false,
        error: 'Image width must be at least 480 px for readability.'
      };
    }

    // Calculate target dimensions (max width 720px, preserve aspect ratio)
    const maxWidth = 720;
    let targetWidth = img.width;
    let targetHeight = img.height;

    if (img.width > maxWidth) {
      targetWidth = maxWidth;
      targetHeight = Math.round((img.height * maxWidth) / img.width);
    }

    // Create canvas for resizing
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    // Try WebP first (better compression), fallback to JPEG
    let compressedBlob = null;
    let format = 'webp';
    let quality = 0.75; // Start with 0.75 quality

    // Try WebP compression
    compressedBlob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/webp', quality);
    });

    // If WebP not supported or too large, try JPEG
    if (!compressedBlob || compressedBlob.size > 300 * 1024) {
      format = 'jpeg';
      quality = 0.7;
      compressedBlob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', quality);
      });
    }

    // If still too large, reduce quality further
    if (compressedBlob.size > 300 * 1024 && compressedBlob.size <= 500 * 1024) {
      quality = 0.65;
      compressedBlob = await new Promise((resolve) => {
        canvas.toBlob(resolve, `image/${format}`, quality);
      });
    }

    // Final size check
    if (compressedBlob.size > 1024 * 1024) { // 1MB hard limit
      return {
        isValid: false,
        error: 'Compressed image exceeds 1 MB. Please use a smaller or less detailed image.'
      };
    }

    // Create File object from Blob
    const extension = format === 'webp' ? 'webp' : 'jpg';
    const compressedFile = new File(
      [compressedBlob],
      `payment-screenshot.${extension}`,
      { type: `image/${format}` }
    );

    return {
      file: compressedFile,
      size: compressedFile.size,
      isValid: true,
      originalSize: file.size,
      compressionRatio: ((1 - compressedFile.size / file.size) * 100).toFixed(1)
    };

  } catch (error) {
    console.error('Image compression error:', error);
    return {
      isValid: false,
      error: 'Failed to compress image. Please try a different file.'
    };
  }
};

/**
 * Load image from file
 * @param {File} file
 * @returns {Promise<HTMLImageElement>}
 */
const loadImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Validate file before compression
 * @param {File} file
 * @returns {{isValid: boolean, error?: string}}
 */
export const validatePaymentScreenshot = (file) => {
  if (!file) {
    return { isValid: false, error: 'No file selected' };
  }

  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
  if (!allowedTypes.includes(file.type)) {
    return { isValid: false, error: 'Only JPEG, PNG, WebP, or PDF files allowed' };
  }

  if (file.size > 5 * 1024 * 1024) {
    return { isValid: false, error: 'File size must be under 5 MB' };
  }

  return { isValid: true };
};

/**
 * Format file size for display
 * @param {number} bytes
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};
